/**
 * Type definitions copied from @indeed/messaging-common-js
 * to remove the dependency. These are simple type definitions
 * that don't require the full library.
 */

// Enums
export enum ActionType {
    VIEW = 'view',
    CLICK = 'click',
    SEND = 'send',
    RECEIVE = 'receive',
    ERROR = 'error',
    SELECT = 'select',
    ARCHIVE = 'archive',
    UNARCHIVE = 'unarchive',
    OPEN_MODERATION_REQUEST = 'open_moderation_request',
    OPEN_DETAIL_PANE = 'open_detail_pane',
    DOWNLOAD = 'download',
    DISMISS = 'dismiss',
    UPLOAD_ATTEMPT = 'upload_attempt',
    OPEN_REZCON_JOB_DESCRIPTION = 'open_rezcon_job_description',
}

export enum ItemType {
    CONVERSATIONS = 'conversations',
    CONVERSATION = 'conversation',
    MESSAGE = 'message',
    MESSAGES = 'messages',
    MESSAGE_ATTACHMENT = 'message_attachment',
    JOB_DETAIL = 'jobDetail',
    BUTTON = 'button',
    LINK = 'link',
    MODAL = 'modal',
    PANE = 'pane',
    FOLDERS = 'folders',
    TEXT = 'text',
    MODERATION_BANNER = 'moderation_banner',
    AI_ASSISTED_MESSAGING = 'ai_assisted_messaging',
    SUGGESTED_MESSAGE_CARD = 'suggested_message_card',
    OUTBOUND_LINK = 'outbound_link',
}

export enum LoggingFolder {
    INBOX = 'inbox',
    ARCHIVE = 'archive',
    BLOCKED = 'blocked',
    SPAM = 'spam',
}

export enum InterviewActions {
    ACCEPT = 'ACCEPT',
    REJECT = 'REJECT',
    CANCEL = 'CANCEL',
    RESCHEDULE = 'RESCHEDULE',
}

export enum CancelReason {
    ACCEPTED_OTHER_JOB = 'ACCEPTED_OTHER_JOB',
    NOT_INTERESTED = 'NOT_INTERESTED',
    OTHER = 'OTHER',
}

// Type definitions
export interface UserActionLog {
    actionType?: ActionType; // Optional for backward compatibility
    itemType: ItemType;
    itemUid?: string;
    itemAttributes?: Record<string, any>;
    action?: ActionType; // Legacy field for backward compatibility
    additionalParams?: Record<string, any>; // Additional parameters for logging
}

export interface ImpressionLog {
    itemType: ItemType;
    itemUids: string[];
    itemsAttributes: Record<string, any>;
    additionalParams?: Record<string, any>; // Additional parameters for logging
}

export interface PageloadLog {
    additionalParams: Record<string, any>;
}

export interface HttpClientResponse {
    status: number;
    data?: any;
    headers?: Record<string, string>;
}

export interface ItemAttributes {
    [key: string]: any;
}

export interface TimeSlot {
    startTime: string;
    endTime: string;
    timeStartMs?: number;
    timeEndMs?: number;
    isConfirmed?: boolean;
}

export interface MessagingServiceInterviewResource {
    interviewId: string;
    interviewRequestId: string;
    status: string;
    timeSlots?: TimeSlot[];
    [key: string]: any;
}

export interface MessagingServiceClientOptions {
    BASE_URL: string;
    DEFAULT_TIMEOUT: number;
    XSRF_COOKIE_NAME: string;
    XSRF_HEADER_NAME: string;
    CLIENT_APP: string;
    LOCALE_LANGUAGE: string;
    LOCALE_COUNTRY: string;
    PAGELOAD_TK: string;
}

export interface OneGraphClientOptions {
    baseUrl?: string;
    API_KEY?: string;
    BASE_URL?: string;
    LOCALE_LANGUAGE?: string;
    LOCALE_COUNTRY?: string;
    PAGELOAD_TK?: string;
    CTK?: string;
    PRFORCE_GROUPS?: string;
    timeout?: number;
    [key: string]: any;
}

export interface HttpClientOptions {
    baseURL: string;
    timeout?: number;
    headers?: Record<string, string>;
    [key: string]: any;
}

export interface IHttpClient {
    get(url: string, config?: any): Promise<HttpClientResponse>;
    post(url: string, data?: any, config?: any): Promise<HttpClientResponse>;
    put(url: string, data?: any, config?: any): Promise<HttpClientResponse>;
    delete(url: string, config?: any): Promise<HttpClientResponse>;
}

// Minimal Messaging Service Client interface
// Interface for client-side messaging service client
// Server-side MessagingServiceClient is a separate class that doesn't implement this interface
export interface IMessagingServiceClient {
    logging: {
        v1: {
            postUserActionLog(pageLoadTk: string, log: UserActionLog): Promise<HttpClientResponse>;
            postImpressionLog(pageLoadTk: string, log: ImpressionLog): Promise<HttpClientResponse>;
        };
    };
}

// Messaging Service Client (for server-side code)
export class MessagingServiceClient {
    public httpClient: {
        header: (name: string, value: string) => void;
        cookie: (name: string, value: string) => void;
        getCookies?: () => Record<string, string>;
        getAxiosClient?: () => any;
    };
    public utils: {
        v1: {
            getCSRFToken: () => Promise<any>;
        };
    };
    public logging: {
        v1: {
            // Only pageload logging on server-side (more stable than client-side)
            postPageloadLog: (pageLoadTk: string, log: PageloadLog) => Promise<HttpClientResponse>;
        };
    };
    private cookies: Record<string, string>;

    constructor(
        options: MessagingServiceClientOptions,
        cookies?: Record<string, string>,
        userAgent?: string
    ) {
        this.cookies = cookies || {};

        this.httpClient = {
            header: () => {},
            cookie: (name: string, value: string) => {
                this.cookies[name] = value;
            },
            getCookies: () => this.cookies,
            getAxiosClient: () => ({
                defaults: {
                    params: {
                        pageLoadTk: options.PAGELOAD_TK,
                    },
                },
            }),
        };
        this.utils = {
            v1: {
                getCSRFToken: async () => ({ data: { data: '' } }),
            },
        };

        // Server-side logging implementation using axios directly
        const axios = require('axios');

        // Ensure country/language are never empty - use defaults as fallback
        const country = options.LOCALE_COUNTRY?.trim() || 'US';
        const language = options.LOCALE_LANGUAGE?.trim() || 'en';

        const buildQueryParams = () => {
            const params = new URLSearchParams({
                clientApp: options.CLIENT_APP || 'MESSAGES_WEB',
                hl: language,
                co: country,
                pageLoadTk: options.PAGELOAD_TK || '',
            });
            return params.toString();
        };

        const buildHeaders = () => {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                'X-Client-App': options.CLIENT_APP || 'MESSAGES_WEB',
                'Accept-Language': `${language}-${country}`,
                'User-Agent': userAgent || 'MESSAGES_WEB',
            };

            // Add CSRF token if available from this.cookies (which gets updated by httpClient.cookie())
            if (options.XSRF_HEADER_NAME && this.cookies[options.XSRF_COOKIE_NAME]) {
                headers[options.XSRF_HEADER_NAME] = this.cookies[options.XSRF_COOKIE_NAME];
            }

            return headers;
        };

        // Build cookie string from this.cookies (which gets updated by httpClient.cookie())
        const buildCookieString = () => {
            return Object.entries(this.cookies)
                .map(([key, value]) => `${key}=${value}`)
                .join('; ');
        };

        this.logging = {
            v1: {
                // Server-side pageload logging only (user actions & impressions stay on FE)
                postPageloadLog: async (pageLoadTk: string, log: PageloadLog) => {
                    const response = await axios.post(
                        `${
                            options.BASE_URL
                        }/messaging/jobseekers/logging/v1/pageLoads?${buildQueryParams()}`,
                        { pageLoadTk, ...log },
                        {
                            headers: { ...buildHeaders(), Cookie: buildCookieString() },
                            // Note: withCredentials is for browsers only - we're manually passing cookies/CSRF
                        }
                    );
                    return {
                        status: response.status,
                        data: response.data,
                        headers: response.headers,
                    };
                },
            },
        };
    }

    static getHealthcheckConfig(urgency: string, runFn: () => Promise<any>) {
        return {
            id: 'messaging-service',
            description: 'Messaging Service healthcheck',
            urgency,
            run: runFn,
        };
    }
}

// Proctor class and types
export class Proctor {
    public proctorGroups: Record<string, any>;
    public proctorGroupsString: string;

    constructor(options: { proctorGroups: Record<string, any>; proctorGroupsString: string }) {
        this.proctorGroups = options.proctorGroups;
        this.proctorGroupsString = options.proctorGroupsString;
    }

    public isInGroup(testName: string, groupName: string): boolean {
        return this.proctorGroups[testName] === groupName;
    }

    public getGroup(testName: string): string | null {
        return this.proctorGroups[testName] || null;
    }

    public getProctorData(): { proctorGroups: Record<string, any>; proctorGroupsString: string } {
        return {
            proctorGroups: this.proctorGroups,
            proctorGroupsString: this.proctorGroupsString,
        };
    }
}

export class HttpClientError extends Error {
    public response?: HttpClientResponse;
    public request?: any;
    public config?: any;

    constructor(message: string, response?: HttpClientResponse, request?: any, config?: any) {
        super(message);
        this.name = 'HttpClientError';
        this.response = response;
        this.request = request;
        this.config = config;
    }
}
